import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import rafSchd from 'raf-schd';
import { useIntl } from 'react-intl-next';
import { TABLE_OVERFLOW_CHANGE_TRIGGER } from '@atlaskit/editor-common/analytics';
import { getGuidelinesWithHighlights } from '@atlaskit/editor-common/guideline';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { focusTableResizer, ToolTipContent } from '@atlaskit/editor-common/keymaps';
import { tableMessages as messages } from '@atlaskit/editor-common/messages';
import { ResizerNext } from '@atlaskit/editor-common/resizer';
import { browser } from '@atlaskit/editor-common/utils';
import { findTable } from '@atlaskit/editor-tables/utils';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { updateWidthToWidest } from '../commands/misc';
import { META_KEYS } from '../pm-plugins/table-analytics';
import { COLUMN_MIN_WIDTH, getColgroupChildrenLength, previewScaleTable, scaleTable, TABLE_MAX_WIDTH } from '../pm-plugins/table-resizing/utils';
import { pluginKey as tableWidthPluginKey } from '../pm-plugins/table-width';
import { TABLE_GUIDELINE_VISIBLE_ADJUSTMENT, TABLE_HIGHLIGHT_GAP, TABLE_HIGHLIGHT_TOLERANCE, TABLE_SNAP_GAP } from '../ui/consts';
import { generateResizedPayload, generateResizeFrameRatePayloads, useMeasureFramerate } from '../utils/analytics';
import { defaultGuidelines, defaultGuidelinesForPreserveTable } from '../utils/guidelines';
import { defaultSnappingWidths, defaultTablePreserveSnappingWidths, findClosestSnap } from '../utils/snapping';
var DEBOUNCE_TIME_FOR_SCREEN_READER_ANNOUNCER = 1000;
var RESIZE_STEP_VALUE = 10;
var handles = {
  right: true
};
var handleStyles = {
  right: {
    // eslint-disable-next-line
    right: '-14px',
    marginTop: "var(--ds-space-150, 12px)"
  }
};
var getResizerHandleHeight = function getResizerHandleHeight(tableRef) {
  var _tableRef$clientHeigh;
  var tableHeight = (_tableRef$clientHeigh = tableRef === null || tableRef === void 0 ? void 0 : tableRef.clientHeight) !== null && _tableRef$clientHeigh !== void 0 ? _tableRef$clientHeigh : 0;
  /*
    - One row table height (minimum possible table height) ~ 45px
    - Two row table height ~ 90px
    - Three row table height ~ 134px
     In the if below we need to use:
    - > 46 because the height of the table can be a float number like 45.44.
    - < 96 is the height of large resize handle.
  */
  if (tableHeight >= 96) {
    return 'large';
  }
  if (tableHeight > 46) {
    return 'medium';
  }
  return 'small';
};
var getResizerMinWidth = function getResizerMinWidth(node) {
  var currentColumnCount = getColgroupChildrenLength(node);
  var minColumnWidth = Math.min(3, currentColumnCount) * COLUMN_MIN_WIDTH;
  // add an extra pixel as the scale table logic will scale columns to be tableContainerWidth - 1
  // the table can't scale past its min-width, so instead restrict table container min width to avoid that situation
  return minColumnWidth + 1;
};

/**
 * When guidelines are outside the viewport, filter them out, do not show
 * So the guideline container won't make the fabric-editor-popup-scroll-parent overflow
 * @param guidelines
 * @param containerWidth editorWidth
 */
var getVisibleGuidelines = function getVisibleGuidelines(guidelines, containerWidth) {
  return guidelines.filter(function (guideline) {
    return guideline.position && guideline.position.x !== undefined && typeof guideline.position.x === 'number' && Math.abs(guideline.position.x * 2) < containerWidth + TABLE_GUIDELINE_VISIBLE_ADJUSTMENT;
  });
};
export var TableResizer = function TableResizer(_ref) {
  var _findTable, _editorView$state;
  var children = _ref.children,
    width = _ref.width,
    maxWidth = _ref.maxWidth,
    containerWidth = _ref.containerWidth,
    updateWidth = _ref.updateWidth,
    onResizeStop = _ref.onResizeStop,
    onResizeStart = _ref.onResizeStart,
    editorView = _ref.editorView,
    getPos = _ref.getPos,
    node = _ref.node,
    tableRef = _ref.tableRef,
    displayGuideline = _ref.displayGuideline,
    attachAnalyticsEvent = _ref.attachAnalyticsEvent,
    displayGapCursor = _ref.displayGapCursor,
    isTableScalingEnabled = _ref.isTableScalingEnabled,
    isWholeTableInDanger = _ref.isWholeTableInDanger,
    pluginInjectionApi = _ref.pluginInjectionApi;
  var currentGap = useRef(0);
  // track resizing state - use ref over state to avoid re-render
  var isResizing = useRef(false);
  var areResizeMetaKeysPressed = useRef(false);
  var _useState = useState(width),
    _useState2 = _slicedToArray(_useState, 2),
    localTableWidth = _useState2[0],
    setLocalTableWidth = _useState2[1];
  var resizerRef = useRef(null);
  var _useSharedPluginState = useSharedPluginState(pluginInjectionApi, ['table']),
    tableState = _useSharedPluginState.tableState;
  var _ref2 = tableState,
    widthToWidest = _ref2.widthToWidest;

  // used to reposition tooltip when table is resizing via keyboard
  var updateTooltip = React.useRef();
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    snappingEnabled = _useState4[0],
    setSnappingEnabled = _useState4[1];

  // we don't want to update aria-live region on each width change, it might provide bad experience for screen reader users
  var _useState5 = useState({
      type: 'none',
      width: width
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    screenReaderResizeInformation = _useState6[0],
    setScreenReaderResizeInformation = _useState6[1];
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var screenReaderResizeAnnouncerMessages = {
    increase: formatMessage(messages.tableSizeIncreaseScreenReaderInformation, {
      newWidth: screenReaderResizeInformation.width
    }),
    decrease: formatMessage(messages.tableSizeDecreaseScreenReaderInformation, {
      newWidth: screenReaderResizeInformation.width
    }),
    none: ''
  };
  var isTableSelected = ((_findTable = findTable((_editorView$state = editorView.state) === null || _editorView$state === void 0 ? void 0 : _editorView$state.selection)) === null || _findTable === void 0 ? void 0 : _findTable.pos) === getPos();
  var resizerMinWidth = getResizerMinWidth(node);
  var handleSize = getResizerHandleHeight(tableRef);
  var _useMeasureFramerate = useMeasureFramerate(),
    startMeasure = _useMeasureFramerate.startMeasure,
    endMeasure = _useMeasureFramerate.endMeasure,
    countFrames = _useMeasureFramerate.countFrames;
  var updateActiveGuidelines = useCallback(function (_ref3) {
    var gap = _ref3.gap,
      keys = _ref3.keys;
    if (gap !== currentGap.current) {
      currentGap.current = gap;
      var visibleGuidelines = getVisibleGuidelines(isTableScalingEnabled ? defaultGuidelinesForPreserveTable(containerWidth) : defaultGuidelines, containerWidth);
      displayGuideline(getGuidelinesWithHighlights(gap, TABLE_SNAP_GAP, keys, visibleGuidelines));
    }
  }, [isTableScalingEnabled, containerWidth, displayGuideline]);
  var guidelineSnaps = useMemo(function () {
    return snappingEnabled ? {
      x: isTableScalingEnabled ? defaultTablePreserveSnappingWidths(containerWidth) : defaultSnappingWidths
    } : undefined;
  }, [snappingEnabled, isTableScalingEnabled, containerWidth]);
  useEffect(function () {
    return function () {
      // only bring back the cursor if this table was deleted - i.e. if a user was resizing, then another
      // deleted this table
      if (isResizing.current) {
        var dispatch = editorView.dispatch,
          _tr = editorView.state.tr;
        displayGapCursor(true);
        displayGuideline([]);
        _tr.setMeta(tableWidthPluginKey, {
          resizing: false
        });
        dispatch(_tr);
      }
    };
  }, [editorView, displayGuideline, displayGapCursor]);
  var handleResizeStart = useCallback(function () {
    startMeasure();
    isResizing.current = true;
    var dispatch = editorView.dispatch,
      tr = editorView.state.tr;
    displayGapCursor(false);
    tr.setMeta(tableWidthPluginKey, {
      resizing: true
    });
    tr.setMeta(META_KEYS.OVERFLOW_TRIGGER, {
      name: TABLE_OVERFLOW_CHANGE_TRIGGER.RESIZED
    });
    dispatch(tr);
    var visibleGuidelines = getVisibleGuidelines(isTableScalingEnabled ? defaultGuidelinesForPreserveTable(containerWidth) : defaultGuidelines, containerWidth);
    setSnappingEnabled(displayGuideline(visibleGuidelines));
    if (getBooleanFF('platform.editor.resizing-table-height-improvement') && onResizeStart) {
      onResizeStart();
    }
  }, [startMeasure, editorView, displayGapCursor, isTableScalingEnabled, containerWidth, displayGuideline, onResizeStart]);
  var handleResize = useCallback(function (originalState, delta) {
    var _node$attrs$localId, _node$attrs, _widestGuideline$posi;
    countFrames();
    var newWidth = originalState.width + delta.width;
    var pos;
    try {
      pos = getPos();
    } catch (e) {
      return;
    }
    if (typeof pos !== 'number') {
      return;
    }
    previewScaleTable(tableRef, {
      node: node,
      prevNode: node,
      start: pos + 1,
      parentWidth: newWidth
    }, editorView.domAtPos.bind(editorView), isTableScalingEnabled);
    updateActiveGuidelines(findClosestSnap(newWidth, isTableScalingEnabled ? defaultTablePreserveSnappingWidths(containerWidth) : defaultSnappingWidths, isTableScalingEnabled ? defaultGuidelinesForPreserveTable(containerWidth) : defaultGuidelines, TABLE_HIGHLIGHT_GAP, TABLE_HIGHLIGHT_TOLERANCE));

    // when isTableScalingEnabled true
    // and a table is resized to fit the widest guideline when view port width is between 1011 and 1800
    // set the width of the table to 1800 pixels.
    var state = editorView.state,
      dispatch = editorView.dispatch;
    var currentTableNodeLocalId = (_node$attrs$localId = node === null || node === void 0 || (_node$attrs = node.attrs) === null || _node$attrs === void 0 ? void 0 : _node$attrs.localId) !== null && _node$attrs$localId !== void 0 ? _node$attrs$localId : '';
    var widestGuideline = defaultGuidelinesForPreserveTable(containerWidth).filter(function (guideline) {
      return guideline.isFullWidth;
    })[0];
    var widestGuideLineWidth = widestGuideline ? (((_widestGuideline$posi = widestGuideline.position) === null || _widestGuideline$posi === void 0 ? void 0 : _widestGuideline$posi.x) || 0) * 2 : null;
    var shouldUpdateWidthToWidest = !!(isTableScalingEnabled && widestGuideLineWidth && Math.abs(widestGuideLineWidth - newWidth) <= 1);
    shouldUpdateWidthToWidest ? setLocalTableWidth(TABLE_MAX_WIDTH) : setLocalTableWidth(newWidth);
    updateWidthToWidest(_defineProperty({}, currentTableNodeLocalId, shouldUpdateWidthToWidest))(state, dispatch);
    updateWidth(shouldUpdateWidthToWidest ? TABLE_MAX_WIDTH : newWidth);
    return newWidth;
  }, [countFrames, isTableScalingEnabled, tableRef, node, editorView, updateActiveGuidelines, containerWidth, updateWidth, getPos]);
  var scheduleResize = useMemo(function () {
    return rafSchd(handleResize);
  }, [handleResize]);
  var handleResizeStop = useCallback(function (originalState, delta) {
    var _node$attrs$localId2, _node$attrs2;
    isResizing.current = false;
    var newWidth = originalState.width + delta.width;
    var state = editorView.state,
      dispatch = editorView.dispatch;
    var pos = getPos();
    var currentTableNodeLocalId = (_node$attrs$localId2 = node === null || node === void 0 || (_node$attrs2 = node.attrs) === null || _node$attrs2 === void 0 ? void 0 : _node$attrs2.localId) !== null && _node$attrs$localId2 !== void 0 ? _node$attrs$localId2 : '';
    newWidth = widthToWidest && currentTableNodeLocalId && widthToWidest[currentTableNodeLocalId] ? TABLE_MAX_WIDTH : newWidth;
    var tr = state.tr.setMeta(tableWidthPluginKey, {
      resizing: false
    });
    var frameRateSamples = endMeasure();
    if (frameRateSamples.length > 0) {
      var resizeFrameRatePayloads = generateResizeFrameRatePayloads({
        docSize: state.doc.nodeSize,
        frameRateSamples: frameRateSamples,
        originalNode: node
      });
      resizeFrameRatePayloads.forEach(function (payload) {
        var _attachAnalyticsEvent;
        (_attachAnalyticsEvent = attachAnalyticsEvent(payload)) === null || _attachAnalyticsEvent === void 0 || _attachAnalyticsEvent(tr);
      });
    }
    if (typeof pos === 'number') {
      var _attachAnalyticsEvent2;
      tr = tr.setNodeMarkup(pos, undefined, _objectSpread(_objectSpread({}, node.attrs), {}, {
        width: newWidth
      }));
      var newNode = tr.doc.nodeAt(pos);
      tr = scaleTable(tableRef, {
        node: newNode,
        prevNode: node,
        start: pos + 1,
        parentWidth: newWidth
      }, editorView.domAtPos.bind(editorView), isTableScalingEnabled)(tr);
      var scaledNode = tr.doc.nodeAt(pos);
      (_attachAnalyticsEvent2 = attachAnalyticsEvent(generateResizedPayload({
        originalNode: node,
        resizedNode: scaledNode
      }))) === null || _attachAnalyticsEvent2 === void 0 || _attachAnalyticsEvent2(tr);
    }
    displayGapCursor(true);
    dispatch(tr);

    // Hide guidelines when resizing stops
    displayGuideline([]);
    updateWidth(newWidth);
    scheduleResize.cancel();
    if (getBooleanFF('platform.editor.resizing-table-height-improvement') && onResizeStop) {
      onResizeStop();
    }
    return newWidth;
  }, [displayGapCursor, updateWidth, editorView, getPos, node, tableRef, scheduleResize, displayGuideline, attachAnalyticsEvent, endMeasure, onResizeStop, isTableScalingEnabled, widthToWidest]);
  var handleTableSizeChangeOnKeypress = useCallback(function (step) {
    var newWidth = width + step;
    if (newWidth > maxWidth || newWidth < resizerMinWidth) {
      return;
    }
    handleResizeStop({
      width: width,
      x: 0,
      y: 0,
      height: 0
    }, {
      width: step,
      height: 0
    });
  }, [width, handleResizeStop, maxWidth, resizerMinWidth]);
  var handleEscape = useCallback(function () {
    editorView === null || editorView === void 0 || editorView.focus();
  }, [editorView]);
  var handleKeyDown = useCallback(function (event) {
    var isBracketKey = event.code === 'BracketRight' || event.code === 'BracketLeft';
    var metaKey = browser.mac ? event.metaKey : event.ctrlKey;
    if (event.altKey || metaKey || event.shiftKey) {
      areResizeMetaKeysPressed.current = true;
    }
    if (event.altKey && metaKey) {
      if (isBracketKey) {
        event.preventDefault();
        handleTableSizeChangeOnKeypress(event.code === 'BracketRight' ? RESIZE_STEP_VALUE : -RESIZE_STEP_VALUE);
      }
    } else if (!areResizeMetaKeysPressed.current) {
      handleEscape();
    }
  }, [handleEscape, handleTableSizeChangeOnKeypress]);
  var handleKeyUp = useCallback(function (event) {
    if (event.altKey || event.metaKey) {
      areResizeMetaKeysPressed.current = false;
    }
    return;
  }, [areResizeMetaKeysPressed]);
  useLayoutEffect(function () {
    if (getBooleanFF('platform.editor.a11y-table-resizing_uapcv')) {
      if (!resizerRef.current) {
        return;
      }
      var resizeHandleThumbEl = resizerRef.current.getResizerThumbEl();
      var globalKeyDownHandler = function globalKeyDownHandler(event) {
        var metaKey = browser.mac ? event.metaKey : event.ctrlKey;
        if (!isTableSelected) {
          return;
        }
        if (event.altKey && event.shiftKey && metaKey && event.code === 'KeyR') {
          event.preventDefault();
          if (!resizeHandleThumbEl) {
            return;
          }
          resizeHandleThumbEl.focus();
          resizeHandleThumbEl.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      };
      var editorViewDom = editorView === null || editorView === void 0 ? void 0 : editorView.dom;
      editorViewDom === null || editorViewDom === void 0 || editorViewDom.addEventListener('keydown', globalKeyDownHandler);
      resizeHandleThumbEl === null || resizeHandleThumbEl === void 0 || resizeHandleThumbEl.addEventListener('keydown', handleKeyDown);
      resizeHandleThumbEl === null || resizeHandleThumbEl === void 0 || resizeHandleThumbEl.addEventListener('keyup', handleKeyUp);
      return function () {
        editorViewDom === null || editorViewDom === void 0 || editorViewDom.removeEventListener('keydown', globalKeyDownHandler);
        resizeHandleThumbEl === null || resizeHandleThumbEl === void 0 || resizeHandleThumbEl.removeEventListener('keydown', handleKeyDown);
        resizeHandleThumbEl === null || resizeHandleThumbEl === void 0 || resizeHandleThumbEl.removeEventListener('keyup', handleKeyUp);
      };
    }
  }, [resizerRef, editorView, handleResizeStop, isTableSelected, handleKeyDown, handleKeyUp]);
  useLayoutEffect(function () {
    if (getBooleanFF('platform.editor.a11y-table-resizing_uapcv')) {
      var _updateTooltip$curren;
      (_updateTooltip$curren = updateTooltip.current) === null || _updateTooltip$curren === void 0 || _updateTooltip$curren.call(updateTooltip);
    }
  }, [width]);
  useEffect(function () {
    if (getBooleanFF('platform.editor.a11y-table-resizing_uapcv')) {
      var debouncedSetWidth = debounce(setScreenReaderResizeInformation, DEBOUNCE_TIME_FOR_SCREEN_READER_ANNOUNCER);
      debouncedSetWidth(function (prevState) {
        var type = 'none';
        if (prevState.width > width) {
          type = 'decrease';
        }
        if (prevState.width < width) {
          type = 'increase';
        }
        return {
          type: type,
          width: width
        };
      });
      return function () {
        debouncedSetWidth.cancel();
      };
    }
  }, [width]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ResizerNext, {
    ref: resizerRef,
    enable: handles,
    width: localTableWidth,
    handleAlignmentMethod: "sticky",
    handleSize: handleSize,
    handleStyles: handleStyles,
    handleResizeStart: handleResizeStart,
    handleResize: scheduleResize,
    handleResizeStop: handleResizeStop,
    resizeRatio: 2,
    minWidth: resizerMinWidth,
    maxWidth: maxWidth,
    snapGap: TABLE_SNAP_GAP,
    snap: guidelineSnaps,
    handlePositioning: "adjacent",
    isHandleVisible: isTableSelected,
    needExtendedResizeZone: !isTableSelected,
    appearance: isTableSelected && isWholeTableInDanger ? 'danger' : undefined,
    handleHighlight: "shadow",
    handleTooltipContent: getBooleanFF('platform.editor.a11y-table-resizing_uapcv') ? function (_ref4) {
      var update = _ref4.update;
      updateTooltip.current = update;
      return /*#__PURE__*/React.createElement(ToolTipContent, {
        description: formatMessage(messages.resizeTable),
        keymap: focusTableResizer
      });
    } : formatMessage(messages.resizeTable)
  }, children), getBooleanFF('platform.editor.a11y-table-resizing_uapcv') && /*#__PURE__*/React.createElement("div", {
    className: "assistive",
    role: "status"
  }, screenReaderResizeAnnouncerMessages[screenReaderResizeInformation.type]));
};